if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.giftConfig = undefined;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const giftVariantId = this.getMatchedGiftVariantId();
        if (giftVariantId) {
          config.headers['Content-Type'] = 'application/json';
          config.body = this.buildBundledAddToCartBody(formData, giftVariantId);
        } else {
          delete config.headers['Content-Type'];
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (giftVariantId) {
                return this.retryAddWithoutGift(formData).then((fallbackResponse) => {
                  if (fallbackResponse.status) {
                    this.handleAddToCartError(fallbackResponse, formData);
                    return;
                  }
                  this.handleAddToCartSuccess(fallbackResponse, formData);
                });
              }

              this.handleAddToCartError(response, formData);
              return;
            }

            this.handleAddToCartSuccess(response, formData);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            // if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      handleAddToCartError(response, formData) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: response.errors || response.description,
          message: response.message,
        });
        this.handleErrorMessage(response.description);

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (!soldOutMessage) return;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        this.error = true;
      }

      handleAddToCartSuccess(response, formData) {
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure("add:paint-updated-sections", () => {
            this.cart.renderContents(response);
          });
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getGiftConfig() {
        if (this.giftConfig !== undefined) return this.giftConfig;

        const configNode = this.querySelector('[data-gift-config]');
        if (!configNode?.textContent) {
          this.giftConfig = null;
          return this.giftConfig;
        }

        try {
          this.giftConfig = JSON.parse(configNode.textContent);
          return this.giftConfig;
        } catch (error) {
          console.error('Failed to parse [data-gift-config]', error);
          this.giftConfig = null;
          return this.giftConfig;
        }
      }

      getVariantQuantityById(variantId) {
        const productInfo = this.closest('product-info');
        if (!productInfo) return null;

        const variantNode = productInfo.querySelector(
          [
            `input[data-variant-id="${variantId}"][data-variant-quantity]`,
            `option[data-variant-id="${variantId}"][data-variant-quantity]`,
            `[data-variant-id="${variantId}"][data-variant-quantity]`,
          ].join(', ')
        );

        const rawQuantity = variantNode?.dataset?.variantQuantity;
        const parsedQuantity = Number(rawQuantity);

        return Number.isFinite(parsedQuantity) ? parsedQuantity : null;
      }

      getMatchedGiftVariantId() {
        const giftConfig = this.getGiftConfig();
        if (!giftConfig) return null;

        const currentVariantId = this.variantIdInput?.value;
        if (!currentVariantId) return null;

        const selectedVariantQuantity = this.getVariantQuantityById(currentVariantId);
        if (!Number.isFinite(selectedVariantQuantity)) return null;

        for (let index = 1; index <= 3; index += 1) {
          const ruleQuantityRaw = giftConfig[`quantity_${index}`];
          const giftVariantIdRaw = giftConfig[`product_${index}_variant_id`];
          const ruleQuantity = Number(ruleQuantityRaw);
          const giftVariantId = Number(giftVariantIdRaw);

          // Guard against empty/invalid config values such as null/"" that coerce to 0.
          if (!Number.isFinite(ruleQuantity) || ruleQuantity <= 0) continue;
          if (!Number.isFinite(giftVariantId) || giftVariantId <= 0) continue;
          if (ruleQuantity !== selectedVariantQuantity) continue;

          return String(giftVariantId);
        }

        return null;
      }

      buildBundledAddToCartBody(formData, giftVariantId) {
        const mainVariantId = formData.get('id');
        if (!mainVariantId) return formData;
        if (!Number.isFinite(Number(giftVariantId)) || Number(giftVariantId) <= 0) return formData;

        const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const mainQuantity = Number(formData.get('quantity') || 1);
        const sellingPlanId = formData.get('selling_plan');
        const sections = formData.get('sections');
        const sectionsUrl = formData.get('sections_url');
        const parentProperties = {
          _bundle_id: bundleId,
          _bundle_role: 'parent',
        };

        for (const [key, value] of formData.entries()) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (!propertyMatch) continue;
          parentProperties[propertyMatch[1]] = value;
        }

        const items = [
          {
            id: Number(mainVariantId),
            quantity: Number.isFinite(mainQuantity) ? mainQuantity : 1,
            properties: parentProperties,
            ...(sellingPlanId ? { selling_plan: Number(sellingPlanId) } : {}),
          },
          {
            id: Number(giftVariantId),
            quantity: 1,
            parent_id: Number(mainVariantId),
            properties: {
              _bundle_id: bundleId,
              _bundle_role: 'gift',
              _is_free_gift: 'true',
            },
          },
        ];

        return JSON.stringify({
          items,
          ...(sections ? { sections } : {}),
          ...(sectionsUrl ? { sections_url: sectionsUrl } : {}),
        });
      }

      retryAddWithoutGift(formData) {
        const fallbackConfig = fetchConfig('javascript');
        fallbackConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete fallbackConfig.headers['Content-Type'];
        fallbackConfig.body = formData;

        return fetch(`${routes.cart_add_url}`, fallbackConfig).then((response) => response.json());
      }
    }
  );
}
